/**
 * SSC-style basic calculator (spec §3.4 step 7): + − × ÷, %, √, ±, memory (MC MR MS M+ M−),
 * backspace and clear. No scientific functions, like the exam's on-screen calculator.
 * Pure state machine so it can be unit-tested.
 */
interface CalcState {
  display: string;
  acc: number | null;
  op: '+' | '-' | '*' | '/' | null;
  /** The next digit starts a new number. */
  fresh: boolean;
  /** The display holds an operand entered since the last operator. */
  operand: boolean;
  memory: number;
  error: boolean;
}

export const initialCalc: CalcState = {
  display: '0',
  acc: null,
  op: null,
  fresh: true,
  operand: false,
  memory: 0,
  error: false,
};

const fmt = (n: number): string => {
  if (!Number.isFinite(n)) return 'Error';
  const s = Number(n.toPrecision(12)).toString();
  return s.length > 16 ? n.toExponential(8) : s;
};

const apply = (a: number, b: number, op: CalcState['op']): number => {
  switch (op) {
    case '+':
      return a + b;
    case '-':
      return a - b;
    case '*':
      return a * b;
    case '/':
      return b === 0 ? Number.NaN : a / b;
    default:
      return b;
  }
};

export type CalcKey =
  | '0'
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | '.'
  | '+'
  | '-'
  | '*'
  | '/'
  | '='
  | 'C'
  | 'CE'
  | 'BS'
  | 'SQRT'
  | 'PCT'
  | 'NEG'
  | 'MC'
  | 'MR'
  | 'MS'
  | 'M+'
  | 'M-';

type Handler = (s: CalcState, cur: number) => CalcState;

/** A finished value on the display, or the error state when it isn't a finite number. */
const result = (s: CalcState, n: number): CalcState =>
  Number.isFinite(n)
    ? { ...s, display: fmt(n), fresh: true, operand: true }
    : { ...s, display: 'Error', error: true };

/** `+ − × ÷`: fold the pending operation into the accumulator and wait for the next operand. */
const operator =
  (op: NonNullable<CalcState['op']>): Handler =>
  (s, cur) => {
    const acc = s.acc === null ? cur : s.operand ? apply(s.acc, cur, s.op) : s.acc;
    if (!Number.isFinite(acc)) return { ...s, display: 'Error', error: true };
    return { ...s, acc, op, display: fmt(acc), fresh: true, operand: false };
  };

const digit =
  (d: string): Handler =>
  (s) => {
    if (s.fresh) return { ...s, display: d, fresh: false, operand: true };
    if (s.display.replace(/[-.]/g, '').length >= 14) return s;
    return { ...s, display: s.display === '0' ? d : s.display + d };
  };

const DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'] as const;

/** What each key does to the state. `cur` is the number on the display. */
const KEYS: Record<CalcKey, Handler> = {
  ...(Object.fromEntries(DIGITS.map((d) => [d, digit(d)])) as Record<
    (typeof DIGITS)[number],
    Handler
  >),
  C: (s) => ({ ...initialCalc, memory: s.memory }),
  CE: (s) => ({ ...s, display: '0', fresh: true, operand: false }),
  BS: (s) => (s.fresh ? s : { ...s, display: s.display.length > 1 ? s.display.slice(0, -1) : '0' }),
  '.': (s) => {
    if (s.fresh) return { ...s, display: '0.', fresh: false, operand: true };
    return s.display.includes('.') ? s : { ...s, display: `${s.display}.` };
  },
  NEG: (s, cur) => ({ ...s, display: fmt(-cur), operand: true }),
  SQRT: (s, cur) => (cur < 0 ? { ...s, display: 'Error', error: true } : result(s, Math.sqrt(cur))),
  // 200 + 10 % → 200 + 20; 200 × 15 % → 200 × 0.15.
  PCT: (s, cur) =>
    result(s, s.acc !== null && (s.op === '+' || s.op === '-') ? (s.acc * cur) / 100 : cur / 100),
  MC: (s) => ({ ...s, memory: 0 }),
  MR: (s) => ({ ...s, display: fmt(s.memory), fresh: true, operand: true }),
  MS: (s, cur) => ({ ...s, memory: cur, fresh: true }),
  'M+': (s, cur) => ({ ...s, memory: s.memory + cur, fresh: true }),
  'M-': (s, cur) => ({ ...s, memory: s.memory - cur, fresh: true }),
  '+': operator('+'),
  '-': operator('-'),
  '*': operator('*'),
  '/': operator('/'),
  '=': (s, cur) => {
    if (s.acc === null || s.op === null) return { ...s, fresh: true };
    return { ...result(s, apply(s.acc, cur, s.op)), acc: null, op: null, operand: false };
  },
};

/** Apply one key press. After an error only `C` does anything. */
export function press(s: CalcState, k: CalcKey): CalcState {
  if (s.error && k !== 'C') return s;
  return KEYS[k](s, Number(s.display));
}
